import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ApiResponse } from "app/core/interfaces/api-response";
import { environment } from "environments/environment";
import { firstValueFrom, Observable } from "rxjs";
import { AdvancePaymentConfig } from "./interfaces/advance-payment-config";
import { BillingInfo } from "./interfaces/billing-info";
import { CartaditoLink } from "./interfaces/cartadito-link";
import { CategoryResponse } from "./interfaces/category-response";
import { ReservationCancellationConfig } from "./interfaces/reservation-cancellation-config";
import { SeatingAreaResponse } from "./interfaces/seating-area-response";
import { UpdateCoordsRequest } from "./interfaces/update-coords-request";
import { UpdateImagesRequest } from "./interfaces/update-images-request";
import { UpdatePasswordRequest } from "./interfaces/update-password-request";
import { UpdateScheduleDto } from "./interfaces/update-schedule-request";
import {
  CompleteWhatsAppOnboardingRequest,
  WhatsAppAccountStatusResponse,
  WhatsAppOnboardingStartResponse,
} from "./interfaces/whatsapp-account";

@Injectable({ providedIn: "root" })
export class SettingsService {
  private apiUrl = environment.apiUrl;
  private _httpClient = inject(HttpClient);

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Update Venue
   *
   * @param object
   */
  updateVenue(id: number, updateRequest): Observable<any> {
    return this._httpClient.put(
      `${this.apiUrl}/venue/update/${id}`,
      updateRequest,
    );
  }

  /**
   * Get advance payment configuration
   *
   * @param id
   */
  getAdvancePaymentConfig(
    id: number,
  ): Observable<ApiResponse<AdvancePaymentConfig>> {
    return this._httpClient.get<ApiResponse<AdvancePaymentConfig>>(
      `${this.apiUrl}/venue/${id}/advance-payment-config`,
    );
  }

  /**
   * Update advance payment configuration
   *
   * @param id
   * @param updateRequest
   */
  updateAdvancePaymentConfig(
    id: number,
    updateRequest: AdvancePaymentConfig,
  ): Observable<ApiResponse<any>> {
    return this._httpClient.put<ApiResponse<any>>(
      `${this.apiUrl}/venue/update/${id}/advance-payment`,
      updateRequest,
    );
  }

  /**
   * Get reservation auto-cancellation configuration
   *
   * @param id
   */
  getReservationCancellationConfig(
    id: number,
  ): Observable<ApiResponse<ReservationCancellationConfig>> {
    return this._httpClient.get<ApiResponse<ReservationCancellationConfig>>(
      `${this.apiUrl}/venue/${id}/reservation-cancellation-config`,
    );
  }

  /**
   * Update reservation auto-cancellation configuration
   *
   * @param id
   * @param updateRequest
   */
  updateReservationCancellationConfig(
    id: number,
    updateRequest: ReservationCancellationConfig,
  ): Observable<ApiResponse<any>> {
    return this._httpClient.put<ApiResponse<any>>(
      `${this.apiUrl}/venue/update/${id}/reservation-cancellation`,
      updateRequest,
    );
  }

  /**
   * Get billing (fiscal) info
   *
   * @param id
   */
  getBillingInfo(id: number): Observable<ApiResponse<BillingInfo>> {
    return this._httpClient.get<ApiResponse<BillingInfo>>(
      `${this.apiUrl}/venue/${id}/billing-info`,
    );
  }

  /**
   * Update billing (fiscal) info
   *
   * @param id
   * @param updateRequest
   */
  updateBillingInfo(
    id: number,
    updateRequest: BillingInfo,
  ): Observable<ApiResponse<any>> {
    return this._httpClient.put<ApiResponse<any>>(
      `${this.apiUrl}/venue/update/${id}/billing-info`,
      updateRequest,
    );
  }

  /** Estado de la vinculación del local con su carta de Cartadito. */
  getCartaditoLink(): Observable<ApiResponse<CartaditoLink>> {
    return this._httpClient.get<ApiResponse<CartaditoLink>>(
      `${this.apiUrl}/venue/integrations/cartadito`,
    );
  }

  /** Conecta el local con el código que el dueño generó en Cartadito. */
  linkCartadito(code: string): Observable<ApiResponse<CartaditoLink>> {
    return this._httpClient.post<ApiResponse<CartaditoLink>>(
      `${this.apiUrl}/venue/integrations/cartadito`,
      { code },
    );
  }

  unlinkCartadito(): Observable<ApiResponse<CartaditoLink>> {
    return this._httpClient.delete<ApiResponse<CartaditoLink>>(
      `${this.apiUrl}/venue/integrations/cartadito`,
    );
  }

  /**
   * Marca un ambiente del local como agotado/no disponible, o lo reabre.
   *
   * @param venueId
   * @param seatingAreaId
   * @param soldOut
   */
  updateSeatingAreaAvailability(
    venueId: number,
    seatingAreaId: number,
    soldOut: boolean,
  ): Observable<ApiResponse<any>> {
    return this._httpClient.put<ApiResponse<any>>(
      `${this.apiUrl}/venue/update/${venueId}/seating-area/${seatingAreaId}/availability`,
      { soldOut },
    );
  }

  /**
   * List categories
   *
   * @param object
   */
  getCategories(): Observable<ApiResponse<CategoryResponse[]>> {
    const type = "admin";
    return this._httpClient.get<ApiResponse<CategoryResponse[]>>(
      `${this.apiUrl}/catalog/categories/${type}`,
    );
  }

  /**
   * List seating areas
   *
   * @param object
   */
  getSeatingAreas(): Observable<ApiResponse<SeatingAreaResponse[]>> {
    return this._httpClient.get<ApiResponse<SeatingAreaResponse[]>>(
      `${this.apiUrl}/catalog/seating-areas`,
    );
  }

  /**
   * List financial institutions
   *
   * @returns Observable with list of financial institutions
   */
  getFinancialInstitutions(): Observable<ApiResponse<any[]>> {
    return this._httpClient.get<ApiResponse<any[]>>(
      `${this.apiUrl}/catalog/financial-institutions`,
    );
  }

  /**
   * Sube un archivo directo a R2 vía presigned URL y confirma la subida
   * (media-storage-ms la valida por contenido y la convierte a JPG antes de
   * dejarla accesible en `publicUrl` — ver su README, sección "Conversión
   * automática a JPG"). Sin el paso de confirmación el archivo queda en
   * staging y `publicUrl` nunca responde nada.
   *
   * @param presignedUrlPath path del gateway que devuelve `{ uploadUrl, publicUrl, stagingKey }`
   * @param file el archivo a subir
   * @param extraBody campos adicionales que necesite ese endpoint (p. ej. `venueName`)
   */
  private async uploadViaPresignedUrl(
    presignedUrlPath: string,
    file: File,
    extraBody: Record<string, unknown> = {},
  ): Promise<string> {
    const presign = await firstValueFrom(
      this._httpClient.post<
        | { uploadUrl: string; publicUrl: string; stagingKey: string }
        | ApiResponse<{ uploadUrl: string; publicUrl: string; stagingKey: string }>
      >(`${this.apiUrl}${presignedUrlPath}`, {
        ...extraBody,
        mimeType: file.type,
      }),
    );
    const { uploadUrl, publicUrl, stagingKey } = (presign as any).data ?? presign;

    await firstValueFrom(
      this._httpClient.put(uploadUrl, file, {
        headers: { "Content-Type": file.type },
      }),
    );

    await firstValueFrom(
      this._httpClient.post<ApiResponse<{ publicUrl: string }> | { publicUrl: string }>(
        `${this.apiUrl}/files/confirm-upload`,
        { key: stagingKey },
      ),
    );

    return publicUrl;
  }

  /**
   * Upload Logo
   *
   * @param file The file to upload
   */
  uploadLogo(file: File): Promise<string> {
    return this.uploadViaPresignedUrl("/files/logo/presigned-url", file);
  }

  /**
   * Upload Banners (también usado para la imagen de promoción)
   *
   * @param file The file to upload
   * @param venueName Nombre del venue, usado para el prefijo de la key en R2
   */
  uploadBanners(file: File, venueName: string): Promise<string> {
    return this.uploadViaPresignedUrl(
      "/files/venue/images/presigned-url",
      file,
      { venueName },
    );
  }

  /**
   * Update Password
   *
   * @param object
   */
  updatePassword(
    updatePasswordRequest: UpdatePasswordRequest,
  ): Observable<ApiResponse<any>> {
    return this._httpClient.post<ApiResponse<any>>(
      `${this.apiUrl}/auth/admin/update-password`,
      updatePasswordRequest,
    );
  }

  /**
   * Update Coords
   *
   * @param object
   */
  updateCoords(
    id: number,
    updateCoordsRequest: UpdateCoordsRequest,
  ): Observable<ApiResponse<any>> {
    return this._httpClient.put<ApiResponse<any>>(
      `${this.apiUrl}/venue/update/coords/${id}`,
      updateCoordsRequest,
    );
  }

  /**
   * Update Coords
   * @id Venue Id
   * @updateImagesRequest Update Images Request
   * @param object
   */
  updateImages(
    id: number,
    updateImagesRequest: UpdateImagesRequest[],
  ): Observable<ApiResponse<any>> {
    return this._httpClient.put<ApiResponse<any>>(
      `${this.apiUrl}/venue/update/images/${id}`,
      updateImagesRequest,
    );
  }

  /**
   * Update Coords
   * @id Venue Id
   * @updateImagesRequest Update Images Request
   * @param object
   */
  updateSchedule(
    id: number,
    updateScheduleRequest: UpdateScheduleDto[],
  ): Observable<ApiResponse<any>> {
    return this._httpClient.put<ApiResponse<any>>(
      `${this.apiUrl}/venue/update/schedule/${id}`,
      updateScheduleRequest,
    );
  }

  /**
   * Estado de conexión de WhatsApp del venue autenticado
   */
  getWhatsAppAccount(): Observable<ApiResponse<WhatsAppAccountStatusResponse>> {
    return this._httpClient.get<ApiResponse<WhatsAppAccountStatusResponse>>(
      `${this.apiUrl}/whatsapp/account`,
    );
  }

  /**
   * appId/configId para lanzar el Embedded Signup de Meta
   */
  startWhatsAppOnboarding(): Observable<
    ApiResponse<WhatsAppOnboardingStartResponse>
  > {
    return this._httpClient.post<ApiResponse<WhatsAppOnboardingStartResponse>>(
      `${this.apiUrl}/whatsapp/onboarding/start`,
      {},
    );
  }

  /**
   * Completa la conexión con el code/waba_id/phone_number_id que entrega
   * el SDK de Embedded Signup
   */
  completeWhatsAppOnboarding(
    request: CompleteWhatsAppOnboardingRequest,
  ): Observable<ApiResponse<any>> {
    return this._httpClient.post<ApiResponse<any>>(
      `${this.apiUrl}/whatsapp/onboarding/callback`,
      request,
    );
  }
}
